// Common RemixIcon presets for quick setup
// Import only the icons you need to keep bundle size small
// This file registers commonly used icons for dynamic lookup in SDK
import {
  RiKeyboardLine,
  RiBookLine,
  RiBugLine,
  RiHomeLine,
  RiHomeFill,
  RiSettingsLine,
  RiSettingsFill,
  RiUserLine,
  RiUserFill,
  RiNotificationLine,
  RiNotificationFill,
  RiSearchLine,
  RiSearchFill,
  RiHeartLine,
  RiHeartFill,
  RiStarLine,
  RiStarFill,
  RiArrowRightLine,
  RiArrowLeftLine,
  RiArrowUpLine,
  RiArrowDownLine,
  RiCheckLine,
  RiCloseLine,
  RiAddLine,
  RiSubtractLine,
  RiEditLine,
  RiDeleteBinLine,
  RiDownloadLine,
  RiUploadLine,
  RiShareLine,
  RiMoreLine,
  RiMenuLine,
  RiCloseCircleLine,
  RiCheckboxCircleLine,
  RiErrorWarningLine,
  RiInformationLine,
  RiQuestionLine,
  RiRocketLine,
  RiLockLine,
  RiLockUnlockLine,
  RiEyeLine,
  RiEyeOffLine,
  RiMailLine,
  RiPhoneLine,
  RiGlobalLine,
  RiCalendarLine,
  RiTimeLine,
  RiFileLine,
  RiFolderLine,
  RiImageLine,
  RiVideoLine,
  RiMusicLine,
  RiBookmarkLine,
  RiBookmarkFill,
  RiFlagLine,
  RiFlagFill,
  RiThumbUpLine,
  RiThumbDownLine,
  RiChat1Line,
  RiChat1Fill,
  RiSendPlaneLine,
  RiSendInsFill,
  RiSendInsLine,
  RiReplyLine,
  RiRefreshLine,
  RiRestartLine,
  RiShutDownLine,
  RiWifiLine,
  RiWifiOffLine,
  RiBluetoothLine,
  RiBatteryLine,
  RiBatteryChargeLine,
  RiFlashlightLine,
  RiVolumeUpLine,
  RiVolumeDownLine,
  RiVolumeMuteLine,
  RiFullscreenLine,
  RiFullscreenExitLine,
  RiZoomInLine,
  RiZoomOutLine,
  RiFocusLine,
  RiCropLine,
  RiFilterLine,
  RiEqualizerLine,
  RiPaletteLine,
  RiBrushLine,
  RiPaintBrushLine,
  RiScissorsLine,
  RiEraserLine,
  RiSquareLine,
  RiSquareFill,
  RiCircleLine,
  RiCircleFill,
  RiTriangleLine,
  RiTriangleFill,
  RiArrowUpCircleLine,
  RiArrowDownCircleLine,
  RiArrowLeftCircleLine,
  RiArrowRightCircleLine,
  RiSkipBackLine,
  RiSkipForwardLine,
  RiPlayLine,
  RiPauseLine,
  RiStopLine,
  RiRecordCircleLine,
  RiRepeatLine,
  RiShuffleLine,
  RiPlayListLine,
  RiPlayListAddLine,
  // Document/Editor Operations
  RiSaveLine,
  RiSaveFill,
  RiSave2Line,
  RiSave2Fill,
  RiFileCopyLine,
  RiFileCopyFill,
  RiClipboardLine,
  RiClipboardFill,
  RiLink,
  RiLinkUnlink,
  RiFileTextLine,
  RiFileTextFill,
  // Business/System
  RiDatabaseLine,
  RiDatabaseFill,
  RiServerLine,
  RiServerFill,
  RiCloudLine,
  RiCloudFill,
  // UI/Forms
  RiCheckboxLine,
  RiCheckboxFill,
  RiRadio2Line,
  RiRadio2Fill,
  // Navigation/Views
  RiGridLine,
  RiGridFill,
  RiListCheck,
  RiShoppingCartLine,
  RiShoppingCartFill,
  RiShoppingBagLine,
  RiShoppingBagFill,
  RiStoreLine,
  RiStoreFill,
  RiPriceTagLine,
  RiPriceTagFill,
  RiGiftLine,
  RiGiftFill,
  RiTrophyLine,
  RiTrophyFill,
  RiAwardLine,
  RiAwardFill,
  RiMedalLine,
  RiMedalFill,
  RiVipCrown2Line,
  RiVipCrown2Fill,
  RiMapPinLine,
  RiMapPinFill,
  RiMapLine,
  RiMapFill,
  RiNavigationLine,
  RiNavigationFill,
  RiCompassLine,
  RiCompassFill,
  RiInformation2Fill,
  RiArrowRightSLine,
  RiQuestionFill,
  // Resource Center block type icons
  RiArrowRightCircleFill,
  RiPagesFill,
  RiListCheck3,
  RiCheckboxCircleFill,
  RiBookOpenFill,
  RiMessage3Fill,
  RiSeparator,
} from '@remixicon/react';
import type { RemixiconComponentType } from '@remixicon/react';
import React from 'react';
import { FlowIcon, ChecklistIcon, LauncherIcon, BannerIcon } from './icon';
import { BuiltinLauncherIcon } from '@usertour/constants';
import { registerIcons } from './remix-icon';

/**
 * Wrap a custom SVG icon component (width/height props) to match RemixiconComponentType (size prop).
 */
const wrapCustomIcon = (
  Icon: React.ForwardRefExoticComponent<any>,
  displayName: string,
): RemixiconComponentType => {
  const Wrapped: RemixiconComponentType = ({ size = 24, color, className, ...rest }) => (
    <Icon
      width={size}
      height={size}
      className={className}
      style={color ? { color } : undefined}
      {...rest}
    />
  );
  (Wrapped as any).displayName = displayName;
  return Wrapped;
};

const FlowIconWrapped = wrapCustomIcon(FlowIcon, 'FlowIconWrapped');
const ChecklistIconWrapped = wrapCustomIcon(ChecklistIcon, 'ChecklistIconWrapped');
const LauncherIconWrapped = wrapCustomIcon(LauncherIcon, 'LauncherIconWrapped');
const BannerIconWrapped = wrapCustomIcon(BannerIcon, 'BannerIconWrapped');

// Register common icons for easy access
// Users can register more icons as needed using registerIcon() or registerIcons()
// Keys are SYMBOL references into the constants enum — an icon's string name is
// written exactly ONCE (in BuiltinLauncherIcon), and `satisfies` locks this map
// complete in both directions: an enum member without a component here, or a
// key outside the enum, fails tsc.
const BUILTIN_ICONS = {
  // Gap fills: names agents reach for that historically silently rendered nothing
  [BuiltinLauncherIcon.KeyboardLine]: RiKeyboardLine,
  [BuiltinLauncherIcon.BookLine]: RiBookLine,
  [BuiltinLauncherIcon.BugLine]: RiBugLine,
  // Base icons for backward compatibility with historical data
  // These icons must be permanently retained and cannot be changed or removed
  // to ensure compatibility with existing user data
  [BuiltinLauncherIcon.InfoCircled]: RiInformationLine,
  [BuiltinLauncherIcon.Rocket]: RiRocketLine,
  [BuiltinLauncherIcon.User]: RiUserFill,
  // Basic UI
  [BuiltinLauncherIcon.HomeLine]: RiHomeLine,
  [BuiltinLauncherIcon.HomeFill]: RiHomeFill,
  [BuiltinLauncherIcon.InformationFill]: RiInformation2Fill,
  [BuiltinLauncherIcon.SettingsLine]: RiSettingsLine,
  [BuiltinLauncherIcon.SettingsFill]: RiSettingsFill,
  [BuiltinLauncherIcon.UserLine]: RiUserLine,
  [BuiltinLauncherIcon.NotificationLine]: RiNotificationLine,
  [BuiltinLauncherIcon.NotificationFill]: RiNotificationFill,
  [BuiltinLauncherIcon.SearchLine]: RiSearchLine,
  [BuiltinLauncherIcon.SearchFill]: RiSearchFill,
  [BuiltinLauncherIcon.HeartLine]: RiHeartLine,
  [BuiltinLauncherIcon.HeartFill]: RiHeartFill,
  [BuiltinLauncherIcon.StarLine]: RiStarLine,
  [BuiltinLauncherIcon.StarFill]: RiStarFill,
  // Navigation
  [BuiltinLauncherIcon.ArrowRightLine]: RiArrowRightLine,
  [BuiltinLauncherIcon.ArrowLeftLine]: RiArrowLeftLine,
  [BuiltinLauncherIcon.ArrowUpLine]: RiArrowUpLine,
  [BuiltinLauncherIcon.ArrowDownLine]: RiArrowDownLine,
  // Actions
  [BuiltinLauncherIcon.CheckLine]: RiCheckLine,
  [BuiltinLauncherIcon.CloseLine]: RiCloseLine,
  [BuiltinLauncherIcon.AddLine]: RiAddLine,
  [BuiltinLauncherIcon.SubtractLine]: RiSubtractLine,
  [BuiltinLauncherIcon.EditLine]: RiEditLine,
  [BuiltinLauncherIcon.DeleteLine]: RiDeleteBinLine,
  [BuiltinLauncherIcon.DeleteBinLine]: RiDeleteBinLine,
  [BuiltinLauncherIcon.DownloadLine]: RiDownloadLine,
  [BuiltinLauncherIcon.UploadLine]: RiUploadLine,
  [BuiltinLauncherIcon.ShareLine]: RiShareLine,
  [BuiltinLauncherIcon.MoreLine]: RiMoreLine,
  [BuiltinLauncherIcon.MenuLine]: RiMenuLine,
  // Status
  [BuiltinLauncherIcon.CloseCircleLine]: RiCloseCircleLine,
  [BuiltinLauncherIcon.CheckCircleLine]: RiCheckboxCircleLine,
  [BuiltinLauncherIcon.CheckboxCircleLine]: RiCheckboxCircleLine,
  [BuiltinLauncherIcon.ErrorWarningLine]: RiErrorWarningLine,
  [BuiltinLauncherIcon.QuestionLine]: RiQuestionLine,
  // Security
  [BuiltinLauncherIcon.LockLine]: RiLockLine,
  [BuiltinLauncherIcon.LockUnlockLine]: RiLockUnlockLine,
  [BuiltinLauncherIcon.EyeLine]: RiEyeLine,
  [BuiltinLauncherIcon.EyeOffLine]: RiEyeOffLine,
  // Communication
  [BuiltinLauncherIcon.MailLine]: RiMailLine,
  [BuiltinLauncherIcon.PhoneLine]: RiPhoneLine,
  [BuiltinLauncherIcon.ChatLine]: RiChat1Line,
  [BuiltinLauncherIcon.ChatFill]: RiChat1Fill,
  [BuiltinLauncherIcon.Chat1Line]: RiChat1Line,
  [BuiltinLauncherIcon.Chat1Fill]: RiChat1Fill,
  [BuiltinLauncherIcon.SendLine]: RiSendPlaneLine,
  [BuiltinLauncherIcon.SendPlaneLine]: RiSendPlaneLine,
  [BuiltinLauncherIcon.SendInsFill]: RiSendInsFill,
  [BuiltinLauncherIcon.SendInsLine]: RiSendInsLine,
  [BuiltinLauncherIcon.ReplyLine]: RiReplyLine,
  // Media
  [BuiltinLauncherIcon.ImageLine]: RiImageLine,
  [BuiltinLauncherIcon.VideoLine]: RiVideoLine,
  [BuiltinLauncherIcon.MusicLine]: RiMusicLine,
  [BuiltinLauncherIcon.FileLine]: RiFileLine,
  [BuiltinLauncherIcon.FolderLine]: RiFolderLine,
  // Social
  [BuiltinLauncherIcon.BookmarkLine]: RiBookmarkLine,
  [BuiltinLauncherIcon.BookmarkFill]: RiBookmarkFill,
  [BuiltinLauncherIcon.FlagLine]: RiFlagLine,
  [BuiltinLauncherIcon.FlagFill]: RiFlagFill,
  [BuiltinLauncherIcon.ThumbUpLine]: RiThumbUpLine,
  [BuiltinLauncherIcon.ThumbDownLine]: RiThumbDownLine,
  // System
  [BuiltinLauncherIcon.RefreshLine]: RiRefreshLine,
  [BuiltinLauncherIcon.RestartLine]: RiRestartLine,
  [BuiltinLauncherIcon.ShutDownLine]: RiShutDownLine,
  [BuiltinLauncherIcon.WifiLine]: RiWifiLine,
  [BuiltinLauncherIcon.WifiOffLine]: RiWifiOffLine,
  [BuiltinLauncherIcon.BluetoothLine]: RiBluetoothLine,
  [BuiltinLauncherIcon.BatteryLine]: RiBatteryLine,
  [BuiltinLauncherIcon.BatteryChargeLine]: RiBatteryChargeLine,
  [BuiltinLauncherIcon.FlashlightLine]: RiFlashlightLine,
  [BuiltinLauncherIcon.VolumeUpLine]: RiVolumeUpLine,
  [BuiltinLauncherIcon.VolumeDownLine]: RiVolumeDownLine,
  [BuiltinLauncherIcon.VolumeMuteLine]: RiVolumeMuteLine,
  [BuiltinLauncherIcon.FullscreenLine]: RiFullscreenLine,
  [BuiltinLauncherIcon.FullscreenExitLine]: RiFullscreenExitLine,
  [BuiltinLauncherIcon.ZoomInLine]: RiZoomInLine,
  [BuiltinLauncherIcon.ZoomOutLine]: RiZoomOutLine,
  [BuiltinLauncherIcon.FocusLine]: RiFocusLine,
  [BuiltinLauncherIcon.CropLine]: RiCropLine,
  [BuiltinLauncherIcon.FilterLine]: RiFilterLine,
  [BuiltinLauncherIcon.EqualizerLine]: RiEqualizerLine,
  [BuiltinLauncherIcon.PaletteLine]: RiPaletteLine,
  [BuiltinLauncherIcon.BrushLine]: RiBrushLine,
  [BuiltinLauncherIcon.PaintBrushLine]: RiPaintBrushLine,
  [BuiltinLauncherIcon.ScissorsLine]: RiScissorsLine,
  [BuiltinLauncherIcon.EraserLine]: RiEraserLine,
  // Shapes
  [BuiltinLauncherIcon.SquareLine]: RiSquareLine,
  [BuiltinLauncherIcon.SquareFill]: RiSquareFill,
  [BuiltinLauncherIcon.CircleLine]: RiCircleLine,
  [BuiltinLauncherIcon.CircleFill]: RiCircleFill,
  [BuiltinLauncherIcon.TriangleLine]: RiTriangleLine,
  [BuiltinLauncherIcon.TriangleFill]: RiTriangleFill,
  // Arrows
  [BuiltinLauncherIcon.ArrowUpCircleLine]: RiArrowUpCircleLine,
  [BuiltinLauncherIcon.ArrowDownCircleLine]: RiArrowDownCircleLine,
  [BuiltinLauncherIcon.ArrowLeftCircleLine]: RiArrowLeftCircleLine,
  [BuiltinLauncherIcon.ArrowRightCircleLine]: RiArrowRightCircleLine,
  // Media Controls
  [BuiltinLauncherIcon.SkipBackLine]: RiSkipBackLine,
  [BuiltinLauncherIcon.SkipForwardLine]: RiSkipForwardLine,
  [BuiltinLauncherIcon.PlayLine]: RiPlayLine,
  [BuiltinLauncherIcon.PauseLine]: RiPauseLine,
  [BuiltinLauncherIcon.StopLine]: RiStopLine,
  [BuiltinLauncherIcon.RecordCircleLine]: RiRecordCircleLine,
  [BuiltinLauncherIcon.RepeatLine]: RiRepeatLine,
  [BuiltinLauncherIcon.ShuffleLine]: RiShuffleLine,
  [BuiltinLauncherIcon.PlayListLine]: RiPlayListLine,
  [BuiltinLauncherIcon.PlayListAddLine]: RiPlayListAddLine,
  // Document/Editor Operations
  [BuiltinLauncherIcon.SaveLine]: RiSaveLine,
  [BuiltinLauncherIcon.SaveFill]: RiSaveFill,
  [BuiltinLauncherIcon.Save2Line]: RiSave2Line,
  [BuiltinLauncherIcon.Save2Fill]: RiSave2Fill,
  [BuiltinLauncherIcon.FileCopyLine]: RiFileCopyLine,
  [BuiltinLauncherIcon.FileCopyFill]: RiFileCopyFill,
  [BuiltinLauncherIcon.ClipboardLine]: RiClipboardLine,
  [BuiltinLauncherIcon.ClipboardFill]: RiClipboardFill,
  [BuiltinLauncherIcon.Link]: RiLink,
  [BuiltinLauncherIcon.LinkUnlink]: RiLinkUnlink,
  [BuiltinLauncherIcon.FileTextLine]: RiFileTextLine,
  [BuiltinLauncherIcon.FileTextFill]: RiFileTextFill,
  // Business/System
  [BuiltinLauncherIcon.DatabaseLine]: RiDatabaseLine,
  [BuiltinLauncherIcon.DatabaseFill]: RiDatabaseFill,
  [BuiltinLauncherIcon.ServerLine]: RiServerLine,
  [BuiltinLauncherIcon.ServerFill]: RiServerFill,
  [BuiltinLauncherIcon.CloudLine]: RiCloudLine,
  [BuiltinLauncherIcon.CloudFill]: RiCloudFill,
  // UI/Forms
  [BuiltinLauncherIcon.CheckboxLine]: RiCheckboxLine,
  [BuiltinLauncherIcon.CheckboxFill]: RiCheckboxFill,
  [BuiltinLauncherIcon.RadioLine]: RiRadio2Line,
  [BuiltinLauncherIcon.RadioFill]: RiRadio2Fill,
  [BuiltinLauncherIcon.Radio2Line]: RiRadio2Line,
  [BuiltinLauncherIcon.Radio2Fill]: RiRadio2Fill,
  // Navigation/Views
  [BuiltinLauncherIcon.GridLine]: RiGridLine,
  [BuiltinLauncherIcon.GridFill]: RiGridFill,
  [BuiltinLauncherIcon.ListCheck]: RiListCheck,
  // Shopping
  [BuiltinLauncherIcon.ShoppingCartLine]: RiShoppingCartLine,
  [BuiltinLauncherIcon.ShoppingCartFill]: RiShoppingCartFill,
  [BuiltinLauncherIcon.ShoppingBagLine]: RiShoppingBagLine,
  [BuiltinLauncherIcon.ShoppingBagFill]: RiShoppingBagFill,
  [BuiltinLauncherIcon.StoreLine]: RiStoreLine,
  [BuiltinLauncherIcon.StoreFill]: RiStoreFill,
  [BuiltinLauncherIcon.PriceTagLine]: RiPriceTagLine,
  [BuiltinLauncherIcon.PriceTagFill]: RiPriceTagFill,
  [BuiltinLauncherIcon.GiftLine]: RiGiftLine,
  [BuiltinLauncherIcon.GiftFill]: RiGiftFill,
  // Awards
  [BuiltinLauncherIcon.TrophyLine]: RiTrophyLine,
  [BuiltinLauncherIcon.TrophyFill]: RiTrophyFill,
  [BuiltinLauncherIcon.AwardLine]: RiAwardLine,
  [BuiltinLauncherIcon.AwardFill]: RiAwardFill,
  [BuiltinLauncherIcon.MedalLine]: RiMedalLine,
  [BuiltinLauncherIcon.MedalFill]: RiMedalFill,
  [BuiltinLauncherIcon.CrownLine]: RiVipCrown2Line,
  [BuiltinLauncherIcon.CrownFill]: RiVipCrown2Fill,
  [BuiltinLauncherIcon.VipCrown2Line]: RiVipCrown2Line,
  [BuiltinLauncherIcon.VipCrown2Fill]: RiVipCrown2Fill,
  // Navigation & Location
  [BuiltinLauncherIcon.MapPinLine]: RiMapPinLine,
  [BuiltinLauncherIcon.MapPinFill]: RiMapPinFill,
  [BuiltinLauncherIcon.MapLine]: RiMapLine,
  [BuiltinLauncherIcon.MapFill]: RiMapFill,
  [BuiltinLauncherIcon.NavigationLine]: RiNavigationLine,
  [BuiltinLauncherIcon.NavigationFill]: RiNavigationFill,
  [BuiltinLauncherIcon.CompassLine]: RiCompassLine,
  [BuiltinLauncherIcon.CompassFill]: RiCompassFill,
  // Time & Date
  [BuiltinLauncherIcon.CalendarLine]: RiCalendarLine,
  [BuiltinLauncherIcon.TimeLine]: RiTimeLine,
  [BuiltinLauncherIcon.GlobalLine]: RiGlobalLine,
  // Misc
  [BuiltinLauncherIcon.ArrowRightSLine]: RiArrowRightSLine,
  // Resource Center block type icons
  [BuiltinLauncherIcon.ArrowRightCircleFill]: RiArrowRightCircleFill,
  [BuiltinLauncherIcon.CheckboxCircleFill]: RiCheckboxCircleFill,
  [BuiltinLauncherIcon.PagesFill]: RiPagesFill,
  [BuiltinLauncherIcon.ListCheck3]: RiListCheck3,
  [BuiltinLauncherIcon.BookOpenFill]: RiBookOpenFill,
  [BuiltinLauncherIcon.Message3Fill]: RiMessage3Fill,
  [BuiltinLauncherIcon.Separator]: RiSeparator,
  // Custom content type icons
  [BuiltinLauncherIcon.FlowIcon]: FlowIconWrapped,
  [BuiltinLauncherIcon.ChecklistIcon]: ChecklistIconWrapped,
  [BuiltinLauncherIcon.LauncherIcon]: LauncherIconWrapped,
  [BuiltinLauncherIcon.BannerIcon]: BannerIconWrapped,
  [BuiltinLauncherIcon.ResourceCenterIcon]: RiQuestionFill,
} satisfies Record<BuiltinLauncherIcon, RemixiconComponentType>;

registerIcons(BUILTIN_ICONS);
